import { useEffect, useState } from "react";

import { AuthenticationFactor, LoginChallenge, securityApi, toLoginChallenge } from "../../backend/security";
import { useAuth } from "../../contexts/Auth";
import { storeSession } from "../../utils/mfa-step-up";

const START_FAILED = "We couldn't start the confirmation. Please try again.";

/**
 * The identifier step offers every address a password or a passkey. Here the account is known,
 * so offer what can actually confirm it: the passkey if it has one, the password if that leads
 * somewhere (no factor yet, or an authenticator code after it). A password alone can't confirm
 * a passkey-only account.
 */
function narrowToAccount(challenge: LoginChallenge, factors: AuthenticationFactor[]): LoginChallenge {
    if (!challenge.availableFactors) {
        return challenge;
    }
    const hasPasskey = factors.some(factor => factor.type === "passkey");
    const passwordLeadsIn = !hasPasskey || factors.some(factor => factor.type === "totp");
    const availableFactors = challenge.availableFactors.filter(method =>
        method.type === "passkey" ? hasPasskey : method.type === "password" ? passwordLeadsIn : true);
    return availableFactors.length ? { ...challenge, availableFactors } : challenge;
}

/** Confirming it's you before a factor change, shared by the classic and the new UI's dialogs */
export function useReauthenticate({ open, onSuccess }: { open: boolean; onSuccess: () => void }) {
    const { profile } = useAuth();
    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [challenge, setChallenge] = useState<LoginChallenge>();
    const [federated, setFederated] = useState(false);
    const [hasPasskey, setHasPasskey] = useState(false);

    useEffect(() => {
        if (!open || !profile?.email) {
            return;
        }
        setError(undefined);
        setChallenge(undefined);
        setLoading(true);
        // Only the latest run may show its challenge; a stale one would remount the dialog mid-prompt
        let current = true;

        // Identifier-first, like the sign-in page: the server decides which
        // methods this account can answer with. Asking for a password up front
        // would offer the one answer that cannot satisfy a factor change.
        securityApi.getOverview()
            .then(async overview => {
                if (!current) {
                    return;
                }
                setFederated(overview.federated);
                setHasPasskey(overview.authenticationFactors.some(factor => factor.type === "passkey"));
                if (overview.federated) {
                    return;
                }
                const started = await securityApi.startLogin(profile.email!);
                const next = toLoginChallenge(started, profile.email);
                if (!current) {
                    return;
                }
                if (next) {
                    setChallenge(narrowToAccount(next, overview.authenticationFactors));
                } else {
                    setError(START_FAILED);
                }
            })
            .catch(() => current && setError(START_FAILED))
            .finally(() => current && setLoading(false));
        return () => { current = false; };
    }, [open, profile?.email]);

    /** A finished sign-in carries tokens; anything else is the next challenge to render. */
    const handleResult = async (result: any) => {
        if (result?.idToken) {
            storeSession(result);
            onSuccess();
            return;
        }
        const next = toLoginChallenge(result, profile?.email);
        if (!next) {
            throw new Error("The sign-in response did not complete.");
        }
        setChallenge(next);
    };

    /** The challenge ended without a sign-in; show why instead of it */
    const abandon = (message: string) => {
        setChallenge(undefined);
        setError(message);
    };

    return { error, loading, challenge, federated, hasPasskey, handleResult, abandon };
}
